/**
 * CacheOptionsPanel
 *
 * Collapsible panel for configuring which engine caches to skip during an audit run.
 * Used in UtilsPage and AuditRunDialog when re-running audits with granular cache control.
 */

import {
  Checkbox,
  CheckboxField,
  Description,
  Label,
} from "@a7y/tailwind-catalyst";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

// =============================================================================
// MARK: Types
// =============================================================================

export interface SkipCacheOptions {
  all: boolean;
  audit: boolean;
  keyword: boolean;
  screenshot: boolean;
  icon: boolean;
  competitor: boolean;
  sentiment: boolean;
  storetext: boolean;
  intent: boolean;
  aiDiscovery: boolean;
}

export type SkipCacheKey = keyof SkipCacheOptions;

export const DEFAULT_SKIP_CACHE_OPTIONS: SkipCacheOptions = {
  all: false,
  audit: false,
  keyword: false,
  screenshot: false,
  icon: false,
  competitor: false,
  sentiment: false,
  storetext: false,
  intent: false,
  aiDiscovery: false,
};

export interface CacheOptionsPanelProps {
  options: SkipCacheOptions;
  onChange: (key: SkipCacheKey, value: boolean) => void;
  expanded: boolean;
  onToggleExpand: () => void;
}

// =============================================================================
// MARK: Constants
// =============================================================================

const ENGINE_LABELS: Record<
  "keyword" | "screenshot" | "icon" | "competitor" | "sentiment" | "storetext" | "intent" | "aiDiscovery",
  string
> = {
  keyword: "Keyword Engine",
  screenshot: "Screenshot Engine",
  icon: "Icon Engine",
  competitor: "Competitor Discovery",
  sentiment: "Sentiment Engine",
  storetext: "Storetext Engine",
  intent: "Intent Engine",
  aiDiscovery: "AI Discovery Engine",
};

// =============================================================================
// MARK: Component
// =============================================================================

export function CacheOptionsPanel({
  options,
  onChange,
  expanded,
  onToggleExpand,
}: CacheOptionsPanelProps) {
  const handleAllChange = (checked: boolean) => {
    onChange("all", checked);
    if (checked) {
      onChange("audit", true);
      onChange("keyword", true);
      onChange("screenshot", true);
      onChange("icon", true);
      onChange("competitor", true);
      onChange("sentiment", true);
      onChange("storetext", true);
      onChange("intent", true);
      onChange("aiDiscovery", true);
    }
  };

  const handleIndividualChange = (
    key: "audit" | "keyword" | "screenshot" | "icon" | "competitor" | "sentiment" | "storetext" | "intent" | "aiDiscovery",
    checked: boolean
  ) => {
    onChange(key, checked);
    if (!checked && options.all) {
      onChange("all", false);
    } else if (checked) {
      const allChecked =
        (key === "audit" || options.audit) &&
        (key === "keyword" || options.keyword) &&
        (key === "screenshot" || options.screenshot) &&
        (key === "icon" || options.icon) &&
        (key === "competitor" || options.competitor) &&
        (key === "sentiment" || options.sentiment) &&
        (key === "storetext" || options.storetext) &&
        (key === "intent" || options.intent) &&
        (key === "aiDiscovery" || options.aiDiscovery);
      if (allChecked) {
        onChange("all", true);
      }
    }
  };

  return (
    <div
      className="border rounded-lg border-gray-200 dark:border-zinc-700 max-w-md"
      data-testid="cache-options-panel"
    >
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-zinc-800/50 rounded-lg transition-colors"
        data-testid="cache-options-toggle"
      >
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Cache Options
        </span>
        <ChevronDownIcon
          className={`size-5 text-gray-400 transition-transform duration-200 ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {expanded && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-zinc-700 space-y-2">
          <CheckboxField>
            <Checkbox
              name="skipAll"
              checked={options.all}
              onChange={handleAllChange}
              data-testid="skip-cache-all-checkbox"
            />
            <Label className="font-medium">Skip All Caches</Label>
          </CheckboxField>

          <div className="border-t border-gray-100 dark:border-zinc-800 my-2" />

          <CheckboxField>
            <Checkbox
              name="skip-audit"
              checked={options.audit}
              onChange={(checked) => handleIndividualChange("audit", checked)}
              data-testid="skip-cache-audit-checkbox"
            />
            <Label>Audit Engine</Label>
            <Description>Re-run audit shaping using cached subengine data</Description>
          </CheckboxField>

          <div className="border-t border-gray-100 dark:border-zinc-800 my-2" />

          <div className="pl-4 space-y-2">
            {(
              Object.entries(ENGINE_LABELS) as [keyof typeof ENGINE_LABELS, string][]
            ).map(([key, label]) => (
              <CheckboxField key={key}>
                <Checkbox
                  name={`skip-${key}`}
                  checked={options[key]}
                  onChange={(checked) => handleIndividualChange(key, checked)}
                  data-testid={`skip-cache-${key}-checkbox`}
                />
                <Label>{label}</Label>
              </CheckboxField>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
